import { Account, AccountType } from '@percent/domain/iam'

/**
 * Order in which an account's types are resolved to a single "primary" type for
 * the search result badge and navigation target. An account can resolve to more
 * than one type (e.g. an internal admin who is also a partner member), so we
 * pick the first one in this order that the current user is allowed to view.
 */
export const ACCOUNT_TYPE_PRECEDENCE: AccountType[] = [
  AccountType.internal,
  AccountType.partner,
  AccountType.organisation
]

export type CanViewAccountType = (type: AccountType) => boolean

export const getPrimaryViewableAccountType = (
  account: Pick<Account, 'type'>,
  canViewType: CanViewAccountType
): AccountType | null =>
  ACCOUNT_TYPE_PRECEDENCE.find(type => (account.type ?? []).includes(type) && canViewType(type)) ?? null

export const getAccountTypeLabel = (type: AccountType, t: (key: string) => string): string => {
  switch (type) {
    case AccountType.partner:
      return t('typography.partner')
    case AccountType.organisation:
      return t('typography.organisation')
    case AccountType.internal:
    default:
      return t('menu.goodstack')
  }
}

/**
 * Builds the member-profile route for an account. Returns null when the route
 * cannot be built (e.g. a partner account missing its partnerId).
 *
 * `type` is null for an account with no known type: the all-account search still
 * surfaces it and sends it to the general account profile (`/accounts/general/:id`),
 * which resolves any account by id and shows no roles/permissions.
 */
export const getAccountDetailRoute = (account: Account, type: AccountType | null): string | null => {
  switch (type) {
    case AccountType.partner:
      return account.partnerId ? `/partners/${account.partnerId}/members/${account.id}` : null
    case AccountType.organisation:
      return account.organisationId ? `/organizations/${account.organisationId}/members/${account.id}` : null
    case AccountType.internal:
      return `/accounts/members/${account.id}`
    case null:
      // A typeless account: the general profile (identity/contact/security only).
      return `/accounts/general/${account.id}`
    default:
      return null
  }
}
